/**
 * PhoneInput — DDI flag picker + masked phone input.
 * Stores: { ddi: "55", valor: "11987921030" } — pure digits, no formatting.
 * Displays: formatted with tenant mask.
 */
import { useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";

// Common countries with DDI codes and flag emoji
export const COUNTRIES = [
  // ── América do Sul ──
  { code: "BR", ddi: "55",  flag: "🇧🇷", name: "Brasil",               mask: "(##) #####-####" },
  { code: "AR", ddi: "54",  flag: "🇦🇷", name: "Argentina",             mask: "(###) ###-####" },
  { code: "CL", ddi: "56",  flag: "🇨🇱", name: "Chile",                 mask: "# #### ####" },
  { code: "CO", ddi: "57",  flag: "🇨🇴", name: "Colômbia",              mask: "### ###-####" },
  { code: "PE", ddi: "51",  flag: "🇵🇪", name: "Peru",                  mask: "### ### ###" },
  { code: "VE", ddi: "58",  flag: "🇻🇪", name: "Venezuela",             mask: "(###) ###-####" },
  { code: "EC", ddi: "593", flag: "🇪🇨", name: "Equador",               mask: "## ### ####" },
  { code: "BO", ddi: "591", flag: "🇧🇴", name: "Bolívia",               mask: "# ### ####" },
  { code: "PY", ddi: "595", flag: "🇵🇾", name: "Paraguai",              mask: "## ### ###" },
  { code: "UY", ddi: "598", flag: "🇺🇾", name: "Uruguai",               mask: "## ### ####" },

  // ── América Central ──
  { code: "MX", ddi: "52",  flag: "🇲🇽", name: "México",                mask: "(##) ####-####" },
  { code: "GT", ddi: "502", flag: "🇬🇹", name: "Guatemala",             mask: "#### ####" },
  { code: "CR", ddi: "506", flag: "🇨🇷", name: "Costa Rica",            mask: "#### ####" },
  { code: "PA", ddi: "507", flag: "🇵🇦", name: "Panamá",                mask: "#### ####" },
  { code: "HN", ddi: "504", flag: "🇭🇳", name: "Honduras",              mask: "#### ####" },
  { code: "SV", ddi: "503", flag: "🇸🇻", name: "El Salvador",           mask: "#### ####" },
  { code: "NI", ddi: "505", flag: "🇳🇮", name: "Nicarágua",             mask: "#### ####" },

  // ── Caribe ──
  { code: "CU", ddi: "53",  flag: "🇨🇺", name: "Cuba",                  mask: "# ### ####" },
  { code: "DO", ddi: "1",   flag: "🇩🇴", name: "Rep. Dominicana",       mask: "(###) ###-####" },
  { code: "HT", ddi: "509", flag: "🇭🇹", name: "Haiti",                 mask: "#### ####" },
  { code: "PR", ddi: "1",   flag: "🇵🇷", name: "Porto Rico",            mask: "(###) ###-####" },
  { code: "TT", ddi: "1",   flag: "🇹🇹", name: "Trinidad e Tobago",     mask: "(###) ###-####" },
  { code: "JM", ddi: "1",   flag: "🇯🇲", name: "Jamaica",               mask: "(###) ###-####" },

  // ── América do Norte ──
  { code: "US", ddi: "1",   flag: "🇺🇸", name: "Estados Unidos",        mask: "(###) ###-####" },
  { code: "CA", ddi: "1",   flag: "🇨🇦", name: "Canadá",                mask: "(###) ###-####" },

  // ── Europa ──
  { code: "PT", ddi: "351", flag: "🇵🇹", name: "Portugal",              mask: "### ### ###" },
  { code: "ES", ddi: "34",  flag: "🇪🇸", name: "Espanha",               mask: "### ### ###" },
  { code: "DE", ddi: "49",  flag: "🇩🇪", name: "Alemanha",              mask: "#### ########" },
  { code: "FR", ddi: "33",  flag: "🇫🇷", name: "França",                mask: "## ## ## ## ##" },
  { code: "IT", ddi: "39",  flag: "🇮🇹", name: "Itália",                mask: "### #### ####" },
  { code: "GB", ddi: "44",  flag: "🇬🇧", name: "Reino Unido",           mask: "#### ######" },

  // ── Outros ──
  { code: "JP", ddi: "81",  flag: "🇯🇵", name: "Japão",                 mask: "##-####-####" },
  { code: "CN", ddi: "86",  flag: "🇨🇳", name: "China",                 mask: "### #### ####" },
  { code: "AU", ddi: "61",  flag: "🇦🇺", name: "Austrália",             mask: "#### ### ###" },
];

/**
 * Apply a #-based mask to a digit-only string.
 * Example: applyMask("11987921030", "(##) #####-####") → "(11) 98792-1030"
 */
export function applyMask(digits, mask) {
  if (!digits || !mask) return digits || "";
  let result = "";
  let di = 0;
  for (let i = 0; i < mask.length && di < digits.length; i++) {
    if (mask[i] === "#") {
      result += digits[di++];
    } else {
      result += mask[i];
    }
  }
  return result;
}

/**
 * Strip a masked value to digits only.
 */
export function stripMask(value) {
  return (value || "").replace(/\D/g, "");
}

/**
 * Format a stored phone { ddi, valor } for display using a mask.
 * Shows: "+55 (11) 98792-1030"
 */
export function formatPhone(tc, mask) {
  if (!tc) return "";
  const digits = tc.valor || "";
  const ddi = tc.ddi || "";
  const formatted = mask ? applyMask(digits, mask) : digits;
  return ddi ? `+${ddi} ${formatted}` : formatted;
}

/**
 * PhoneInput component.
 * Props:
 *   value: { ddi: "55", valor: "11987921030" }
 *   onChange: (newValue) => void
 *   mask: "(##) #####-####"  — from tenant settings
 *   defaultDdi: "55"
 *   defaultCountry: "BR"
 *   placeholder: optional
 */
export default function PhoneInput({
  value, onChange, mask = "(##) #####-####",
  defaultDdi = "55", defaultCountry = "BR",
  placeholder = "",
}) {
  const [open, setOpen] = useState(false);

  const currentDdi = value?.ddi || defaultDdi;
  const currentCountry = COUNTRIES.find(c => c.ddi === currentDdi) ||
    COUNTRIES.find(c => c.code === defaultCountry) || COUNTRIES[0];

  const displayValue = value?.valor ? applyMask(value.valor, mask) : "";

  const handleInputChange = (e) => {
    const digits = stripMask(e.target.value);
    const maxDigits = (mask.match(/#/g) || []).length;
    onChange({ ddi: currentDdi, valor: digits.slice(0, maxDigits) });
  };

  return (
    <div className="flex gap-2">
      {/* DDI selector using Shadcn Popover+Command — works correctly inside Dialogs */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 h-9 px-2.5 border border-slate-200 rounded-md bg-white hover:border-slate-300 transition-colors text-sm flex-shrink-0"
          >
            <span className="text-base leading-none">{currentCountry.flag}</span>
            <span className="text-xs font-medium text-slate-600">+{currentDdi}</span>
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-64" align="start" side="bottom">
          <Command>
            <CommandInput placeholder="Buscar país ou DDI..." className="h-9" />
            <CommandList className="max-h-52">
              <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
              <CommandGroup>
                {COUNTRIES.map(c => (
                  <CommandItem
                    key={c.code + c.ddi}
                    value={`${c.name} ${c.ddi} ${c.code}`}
                    onSelect={() => {
                      onChange({ ddi: c.ddi, valor: value?.valor || "" });
                      setOpen(false);
                    }}
                    className="flex items-center gap-2.5 cursor-pointer"
                  >
                    <span className="text-base leading-none">{c.flag}</span>
                    <span className="flex-1 text-sm">{c.name}</span>
                    <span className="text-xs text-slate-400 font-mono">+{c.ddi}</span>
                    {c.ddi === currentDdi && <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Masked phone input */}
      <input
        type="tel"
        value={displayValue}
        onChange={handleInputChange}
        placeholder={placeholder || applyMask("00000000000", mask)}
        className="flex-1 h-9 px-3 border border-slate-200 rounded-md text-sm outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 transition-colors bg-white"
      />
    </div>
  );
}
